import { BALANCE } from './balance';
import type { Rng } from './rng';
import type { Cell, Direction, EntityId, Transition } from './rules/contracts';

/** A power of two. Runtime validation protects the boundary where data enters. */
export type TileValue = number;

export interface BoardTile {
  readonly id: EntityId;
  readonly value: TileValue;
  readonly cell: Cell;
}

export interface BoardState {
  readonly size: number;
  readonly tiles: readonly BoardTile[];
  /** Monotonic source for stable tile ids. */
  readonly nextTileId: number;
}

export interface BoardMerge {
  readonly survivorId: EntityId;
  readonly consumedId: EntityId;
  readonly at: Cell;
  readonly value: TileValue;
}

export interface BoardMoveResult {
  readonly state: BoardState;
  readonly transitions: readonly Transition[];
  readonly merges: readonly BoardMerge[];
  readonly moved: boolean;
  readonly scoreGain: number;
  readonly spawned: BoardTile | null;
}

export interface SpawnResult {
  readonly state: BoardState;
  readonly transition: Transition | null;
  readonly tile: BoardTile | null;
}

export interface StalledBoardRecovery {
  readonly state: BoardState;
  readonly transitions: readonly Transition[];
  readonly removed: readonly BoardTile[];
  readonly hpPenalty: number;
}

const cellKey = (cell: Cell): string => `${cell.col},${cell.row}`;

export function createBoardState(
  tiles: readonly BoardTile[] = [],
  size: number = BALANCE.board.size,
  nextTileId = 1,
): BoardState {
  if (!Number.isInteger(size) || size < 2) throw new Error('Board size must be an integer >= 2.');

  const ids = new Set<string>();
  const cells = new Set<string>();
  const copy = tiles.map((tile) => {
    assertTile(tile, size);
    if (ids.has(tile.id)) throw new Error(`Duplicate tile id: ${tile.id}`);
    const key = cellKey(tile.cell);
    if (cells.has(key)) throw new Error(`Two tiles occupy cell ${key}.`);
    ids.add(tile.id);
    cells.add(key);
    return { ...tile, cell: { ...tile.cell } };
  });

  return { size, tiles: copy, nextTileId };
}

/** Creates the standard two-tile opening board from an explicit seeded RNG. */
export function createInitialBoard(random: Rng): BoardState {
  let state = createBoardState();
  for (let index = 0; index < BALANCE.board.initialTiles; index += 1) {
    state = spawnTile(state, random).state;
  }
  return state;
}

/**
 * Pure 2048 reduction. The caller owns the RNG stream, so the server and an
 * optimistic client can replay the same input from the same seed.
 */
export function moveBoard(state: BoardState, direction: Direction, random: Rng): BoardMoveResult {
  const original = createBoardState(state.tiles, state.size, state.nextTileId);
  const byCell = new Map(original.tiles.map((tile) => [cellKey(tile.cell), tile] as const));
  const nextTiles: BoardTile[] = [];
  const transitions: Transition[] = [];
  const merges: BoardMerge[] = [];

  for (const line of movementLines(original.size, direction)) {
    const occupied = line
      .map((cell) => byCell.get(cellKey(cell)))
      .filter((tile): tile is BoardTile => tile !== undefined);

    let read = 0;
    let write = 0;
    while (read < occupied.length) {
      const survivor = occupied[read] as BoardTile;
      const consumed = occupied[read + 1];
      const target = line[write] as Cell;

      if (consumed && consumed.value === survivor.value) {
        if (!sameCell(survivor.cell, target)) {
          transitions.push({
            kind: 'move',
            id: survivor.id,
            from: survivor.cell,
            to: target,
            stage: 0,
          });
        }

        const value = survivor.value * 2;
        transitions.push({
          kind: 'merge',
          id: consumed.id,
          from: consumed.cell,
          to: target,
          into: survivor.id,
          value,
          stage: 0,
        });
        merges.push({ survivorId: survivor.id, consumedId: consumed.id, at: target, value });
        nextTiles.push({ id: survivor.id, value, cell: { ...target } });
        read += 2;
      } else {
        if (!sameCell(survivor.cell, target)) {
          transitions.push({
            kind: 'move',
            id: survivor.id,
            from: survivor.cell,
            to: target,
            stage: 0,
          });
        }
        nextTiles.push({ ...survivor, cell: { ...target } });
        read += 1;
      }
      write += 1;
    }
  }

  const moved = transitions.length > 0;
  if (!moved) {
    return {
      state: original,
      transitions: [],
      merges: [],
      moved: false,
      scoreGain: 0,
      spawned: null,
    };
  }

  const collapsed = createBoardState(nextTiles, original.size, original.nextTileId);
  const spawn = spawnTile(collapsed, random, 1);
  return {
    state: spawn.state,
    transitions: spawn.transition ? [...transitions, spawn.transition] : transitions,
    merges,
    moved: true,
    scoreGain:
      merges.reduce((sum, merge) => sum + merge.value, 0) * BALANCE.scoring.pointsPerMergedValue,
    spawned: spawn.tile,
  };
}

/** Adds a 2 (90%) or 4 (10%) to a uniformly selected empty cell. */
export function spawnTile(state: BoardState, random: Rng, stage = 0): SpawnResult {
  const occupied = new Set(state.tiles.map((tile) => cellKey(tile.cell)));
  const empty: Cell[] = [];
  for (let row = 0; row < state.size; row += 1) {
    for (let col = 0; col < state.size; col += 1) {
      const cell = { col, row };
      if (!occupied.has(cellKey(cell))) empty.push(cell);
    }
  }
  if (empty.length === 0) return { state, transition: null, tile: null };

  const chosen = empty[randomIndex(random, empty.length)] as Cell;
  const value = random() < BALANCE.board.spawnValue2Chance ? 2 : 4;
  let sequence = state.nextTileId;
  const ids = new Set(state.tiles.map((tile) => tile.id));
  while (ids.has(`tile-${sequence}`)) sequence += 1;

  const tile: BoardTile = { id: `tile-${sequence}`, value, cell: { ...chosen } };
  return {
    state: {
      ...state,
      tiles: [...state.tiles, tile],
      nextTileId: sequence + 1,
    },
    transition: { kind: 'spawn', id: tile.id, at: tile.cell, value: tile.value, stage },
    tile,
  };
}

export function hasAvailableMoves(state: BoardState): boolean {
  if (state.tiles.length < state.size * state.size) return true;
  const byCell = new Map(state.tiles.map((tile) => [cellKey(tile.cell), tile] as const));
  for (const tile of state.tiles) {
    const right = byCell.get(cellKey({ col: tile.cell.col + 1, row: tile.cell.row }));
    const down = byCell.get(cellKey({ col: tile.cell.col, row: tile.cell.row + 1 }));
    if (right?.value === tile.value || down?.value === tile.value) return true;
  }
  return false;
}

/** Implements the non-terminal stalled-board rule and reports its HP cost. */
export function recoverStalledBoard(state: BoardState): StalledBoardRecovery {
  if (hasAvailableMoves(state)) {
    return { state, transitions: [], removed: [], hpPenalty: 0 };
  }

  const removed = [...state.tiles]
    .sort(
      (a, b) =>
        a.value - b.value ||
        a.cell.row - b.cell.row ||
        a.cell.col - b.cell.col ||
        a.id.localeCompare(b.id),
    )
    .slice(0, BALANCE.board.stalledTilesRemoved);
  const removedIds = new Set(removed.map((tile) => tile.id));
  return {
    state: { ...state, tiles: state.tiles.filter((tile) => !removedIds.has(tile.id)) },
    transitions: removed.map((tile) => ({
      kind: 'remove' as const,
      id: tile.id,
      from: tile.cell,
      stage: 0,
    })),
    removed,
    hpPenalty: BALANCE.versus.stalledBoardHpPenalty,
  };
}

export function boardValues(state: BoardState): number[][] {
  const values = Array.from({ length: state.size }, () => Array<number>(state.size).fill(0));
  for (const tile of state.tiles) (values[tile.cell.row] as number[])[tile.cell.col] = tile.value;
  return values;
}

function movementLines(size: number, direction: Direction): Cell[][] {
  const lines: Cell[][] = [];
  for (let fixed = 0; fixed < size; fixed += 1) {
    const line: Cell[] = [];
    for (let moving = 0; moving < size; moving += 1) {
      const offset = direction === 'right' || direction === 'down' ? size - 1 - moving : moving;
      line.push(
        direction === 'left' || direction === 'right'
          ? { col: offset, row: fixed }
          : { col: fixed, row: offset },
      );
    }
    lines.push(line);
  }
  return lines;
}

function sameCell(a: Cell, b: Cell): boolean {
  return a.col === b.col && a.row === b.row;
}

function randomIndex(random: Rng, length: number): number {
  const sample = random();
  if (!Number.isFinite(sample) || sample < 0 || sample >= 1) {
    throw new Error(`RNG must return a finite number in [0, 1); received ${sample}.`);
  }
  return Math.floor(sample * length);
}

function assertTile(tile: BoardTile, size: number): void {
  if (!tile.id) throw new Error('Tile id cannot be empty.');
  if (!Number.isInteger(tile.value) || tile.value < 2 || !Number.isInteger(Math.log2(tile.value))) {
    throw new Error(`Tile ${tile.id} has invalid value ${tile.value}; expected a power of two.`);
  }
  if (
    !Number.isInteger(tile.cell.col) ||
    !Number.isInteger(tile.cell.row) ||
    tile.cell.col < 0 ||
    tile.cell.row < 0 ||
    tile.cell.col >= size ||
    tile.cell.row >= size
  ) {
    throw new Error(`Tile ${tile.id} is outside the ${size}x${size} board.`);
  }
}
